// ===========================================================================
// Telling you a message arrived while you were looking somewhere else.
//
// A chat here is a browser tab among a person's other browser tabs. Somebody
// who alt-tabs away mid-conversation has no way of knowing the other person
// answered: the message lands in a window they cannot see, and the only cue is
// going back to look. That is a bad way to hold a conversation with a stranger
// who is waiting, and it is worse in a group room, where the answer may be to
// something you asked.
//
// So: while the tab is AWAY, arriving messages are counted, the count goes in
// the tab title -- "(3) Lumi: ..." -- and a short tone plays. Coming back
// clears all of it. Nothing is shown while you are here, because while you are
// here you can see the message.
//
// Deliberately NOT the Notification API. A real OS notification needs a
// permission prompt, and decision H3 is that notifications are in-app for this
// build: no push, no tokens, nothing that leaves the tab. A title badge asks
// nobody for anything and works in every browser without being granted.
//
// "Away" is hidden OR unfocused. document.hidden alone misses the common case
// -- the tab is still the active tab, but the window is behind something else,
// so it is "visible" and unwatched. Both count.
//
// ASCII only.
// ===========================================================================

import { playMessageTone, playJoinTone } from './tones.js';

/** The title the page loaded with, restored whenever the count returns to 0. */
const BASE_TITLE = typeof document === 'undefined' ? '' : document.title;

/** A burst of messages is one interruption, not six. */
export const TONE_THROTTLE_MS = 3000;

/** How high the badge counts before it stops being a number worth reading. */
export const BADGE_MAX = 9;

let unread = 0;
// Somebody connected while you were looking elsewhere. Not a count: one match
// is not "3 matches", and the thing you want to know is that the waiting is
// over. It outranks the unread badge in the title for the same reason.
let matched = false;
// An age check this page was waiting on has been decided. See noteDecided.
let decided = false;
// "Never", so the first message always sounds. Zero would be a real instant --
// harmless against a wall clock, wrong against any injected one.
let lastToneAt = -Infinity;
let started = false;
let faviconEl = null;
let originalFaviconHref = null;

/**
 * Is the person somewhere else? Hidden covers a background tab; hasFocus covers
 * a foreground tab in a window behind another window. Either means they cannot
 * see the message arrive.
 */
export function isAway(doc = typeof document === 'undefined' ? null : document) {
  if (!doc) return false;
  if (doc.hidden) return true;
  return typeof doc.hasFocus === 'function' ? !doc.hasFocus() : false;
}

/** The title for a given count. Exported so a test can read it without a DOM. */
export function titleFor(count, base = BASE_TITLE) {
  if (!count || count < 1) return base;
  return `(${count > BADGE_MAX ? `${BADGE_MAX}+` : count}) ${base}`;
}

/**
 * The title while somebody is waiting for you in a chat you are not looking at.
 *
 * A word, not a number. Lumi is new and quiet, and the honest advice to
 * somebody who finds nobody is to leave the tab open and go and do something
 * else -- which only works if the tab can shout when it matters. A "(1)" in
 * front of the usual title does not read as "the thing you were waiting for
 * has happened".
 */
export function matchedTitle(base = BASE_TITLE) {
  return `Someone is here! - ${base}`;
}

/**
 * The title while an age check somebody is waiting on has been decided.
 *
 * The same job matchedTitle does, for the other thing worth leaving a tab open
 * for. Half of every check lands on a person, so the screen tells people their
 * place is held and they can close the page and come back; the ones who leave
 * the tab open and look away had nothing to bring them back at all, and an
 * approval made twenty-eight seconds later reached nobody (the operator,
 * 2026-09-20).
 */
export function decidedTitle(base = BASE_TITLE) {
  return `Your age check is done - ${base}`;
}

/** How many messages have arrived since the person last looked. */
export function unreadCount() {
  return unread;
}

// ---- The favicon dot ---------------------------------------------------------
//
// The title badge is invisible in a browser showing eight pinned tabs as icons.
// A dot drawn over the icon survives that. Canvas to a data: URL, which the
// CSP allows (img-src includes data:); if anything here fails the title badge
// has already done the job, so every step is best effort.

function paintFavicon(count) {
  if (typeof document === 'undefined') return;
  try {
    if (!faviconEl) {
      faviconEl = document.querySelector('link[rel="icon"]');
      originalFaviconHref = faviconEl ? faviconEl.getAttribute('href') : null;
    }
    if (!faviconEl) return;
    if (!count) {
      if (originalFaviconHref) faviconEl.setAttribute('href', originalFaviconHref);
      return;
    }
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const g = canvas.getContext('2d');
    if (!g) return;
    // The orb, roughly: Lumi's violet, so the tab still looks like Lumi.
    g.fillStyle = '#8b5cf6';
    g.beginPath();
    g.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
    g.fill();
    // The dot, high-contrast, upper right.
    g.fillStyle = '#f43f5e';
    g.beginPath();
    g.arc(size - 18, 18, 16, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = '#ffffff';
    g.font = 'bold 22px system-ui, sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(count > BADGE_MAX ? '9+' : String(count), size - 18, 19);
    faviconEl.setAttribute('href', canvas.toDataURL('image/png'));
  } catch {
    /* a browser that refuses canvas or data: URLs still gets the title badge */
  }
}

function paint() {
  if (typeof document === 'undefined') return;
  // A decided check outranks a match: it is the door, and nothing behind it
  // matters until it opens.
  if (decided) document.title = decidedTitle();
  else document.title = matched ? matchedTitle() : titleFor(unread);
  // The dot is the same dot either way: something happened here.
  paintFavicon(decided || matched ? Math.max(unread, 1) : unread);
}

/**
 * A message arrived. Counts and sounds ONLY when the person is away; when they
 * are here, the message is already on their screen and needs no announcing.
 *
 * Returns what it decided rather than just "yes": the caller ignores it, but it
 * is the only way a test can watch the throttle without reimplementing the
 * arithmetic and proving nothing.
 * @returns {{ counted: boolean, toned: boolean }}
 */
export function noteMessage({ now = Date.now() } = {}) {
  if (!isAway()) return { counted: false, toned: false };
  unread += 1;
  paint();
  // One tone per burst. Six messages landing together is one interruption, and
  // a tone per message is the thing that makes somebody mute the tab.
  const toned = now - lastToneAt >= TONE_THROTTLE_MS;
  if (toned) {
    lastToneAt = now;
    playMessageTone();
  }
  return { counted: true, toned };
}

/**
 * You have been paired. Sounds and badges the tab ONLY when the person is
 * away, like a message -- but with the arrival tone rather than the message
 * one, and with no throttle: being connected happens once per chat, it is the
 * event the person has been waiting for, and it is never part of a burst.
 * @returns {{ announced: boolean }}
 */
/**
 * The age check was decided while they were away.
 *
 * Told the same way being paired is: the title says so and the tone plays, so
 * somebody who left the tab open and looked away is brought back. Nothing is
 * announced to somebody who is already looking, because the screen in front of
 * them has already changed.
 *
 * Both answers, not only the good one: a person told nothing after a refusal
 * sits on a screen that will never change again.
 * @returns {{ announced: boolean }}
 */
export function noteDecided() {
  if (!isAway()) return { announced: false };
  decided = true;
  paint();
  playJoinTone();
  return { announced: true };
}

export function noteMatched() {
  if (!isAway()) return { announced: false };
  matched = true;
  paint();
  playJoinTone();
  return { announced: true };
}

/** Whether the tab is currently announcing a match. */
export function matchedPending() {
  return matched;
}

/** They looked. Put everything back. */
export function clearAttention() {
  if (unread === 0 && !matched && !decided) return;
  unread = 0;
  matched = false;
  decided = false;
  paint();
}

/**
 * Start watching. Idempotent, so calling it from more than one place is safe.
 * Returns a stop function, which the tests use and the app does not need.
 */
export function startTabAttention() {
  if (started || typeof document === 'undefined') return () => {};
  started = true;
  const onBack = () => {
    if (!isAway()) clearAttention();
  };
  document.addEventListener('visibilitychange', onBack);
  window.addEventListener('focus', onBack);
  return () => {
    document.removeEventListener('visibilitychange', onBack);
    window.removeEventListener('focus', onBack);
    started = false;
  };
}

/** Tests only: forget the count and the throttle without touching the DOM. */
export function _reset() {
  unread = 0;
  matched = false;
  decided = false;
  lastToneAt = -Infinity;
  started = false;
  faviconEl = null;
  originalFaviconHref = null;
}

export default {
  isAway,
  titleFor,
  matchedTitle,
  unreadCount,
  noteMessage,
  noteMatched,
  matchedPending,
  clearAttention,
  startTabAttention,
  BADGE_MAX,
  TONE_THROTTLE_MS,
};
